import { zodResolver } from "@hookform/resolvers/zod";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { adminLogin } from "./api/application-api";
import AuthSubmitButton from "./components/AuthSubmitButton";
import { useAuth } from "./context/auth-context";
import { Button } from "./components/ui/button";

import logo from "@/assets/logo/promptlayer-cake-color.png";
import { FormInput } from "@/components/ui/FormInput";
import { displayErrorToast } from "@/utils/toast";

const adminLoginSchema = z.object({
  admin_email: z
    .string()
    .email({ message: "Invalid email format" })
    .nonempty({ message: "Admin email is required" }),
  password: z.string().nonempty({ message: "Password is required" }),
  target_user_email: z
    .string()
    .email({ message: "Invalid email format" })
    .nonempty({ message: "Target user email is required" }),
});

const AdminLogin: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkInput, setLinkInput] = useState<string>(searchParams.get("link") || "");

  const form = useForm<z.infer<typeof adminLoginSchema>>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      admin_email: "",
      password: "",
      target_user_email: searchParams.get("as") || "",
    },
    resolver: zodResolver(adminLoginSchema),
  });

  const updateUrl = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("link", linkInput);
    newParams.set("as", form.getValues("target_user_email"));
    setSearchParams(newParams);
  };

  const onSubmit = async (values: z.infer<typeof adminLoginSchema>) => {
    setLoading(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await adminLogin({
        admin_email: values.admin_email,
        password: values.password,
        target_user_email: values.target_user_email,
      });

      if (response.ok) {
        const { access_token, user } = await response.json();

        auth?.login(access_token, user);
        navigate("/home");
        return;
      }

      const { message } = await response.json();
      displayErrorToast(
        typeof message === "string"
          ? message
          : "Unexpected server response format. Please try again later.",
      );
    } catch {
      displayErrorToast("Something went wrong. Please try again later.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2">
        <img src={logo} alt="logo" className="h-[52px] w-[52px]" />
        <span className="text-3xl font-bold text-gray-900">Admin Login</span>
      </div>
      <div className="flex flex-col gap-3">
        <form className="flex flex-col gap-3 pt-3" onSubmit={form.handleSubmit(onSubmit)}>
          <FormInput
            label="Admin Email"
            placeholder="Admin Email"
            type="text"
            error={form.formState.errors.admin_email?.message}
            {...form.register("admin_email")}
          />
          <FormInput
            label="Admin Password"
            placeholder="Admin Password"
            type="password"
            error={form.formState.errors.password?.message}
            {...form.register("password")}
          />
          <FormInput
            label="Target User Email"
            placeholder="Target User Email"
            type="text"
            error={form.formState.errors.target_user_email?.message}
            {...form.register("target_user_email")}
            onBlur={updateUrl}
          />
          <FormInput
            name="link"
            label="Link (optional)"
            placeholder="Link (optional)"
            type="text"
            onBlur={updateUrl}
            value={linkInput}
            onChange={e => setLinkInput(e.target.value)}
          />
          <AuthSubmitButton loading={loading} text="Admin Login" />
        </form>
        <div className="text-center text-sm font-light text-gray-800">
          Not an admin?{" "}
          <Button variant="inlineLink" onClick={() => navigate("/login")}>
            Go to regular login.
          </Button>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
